import { Module, GetterTree, MutationTree, ActionTree } from "vuex";
import { RootState } from "@/store/types";
import { NavigationRailItem } from "@/api/models/NavigationRailItem";
import { CompanyFeaturesService } from "@/api/services/CompanyFeaturesService";

export type NavigationState = {
  features: Array<NavigationRailItem> | null;
  allFeatures: Array<NavigationRailItem> | null;
};

const state: NavigationState = {
  features: null,
  allFeatures: null,
};

const getters: GetterTree<NavigationState, RootState> = {
  features: state => state.features,
  allFeatures: state => state.allFeatures,
};

const mutations: MutationTree<NavigationState> = {
  SET_FEATURES: (state, features: Array<NavigationRailItem> | null) => {
    state.features = features;
  },
  SET_ALL_FEATURES: (state, allFeatures: Array<NavigationRailItem> | null) => {
    state.allFeatures = allFeatures;
  },
};

const actions: ActionTree<NavigationState, RootState> = {
  /**
   * Fetch enabled features for navigation rail.
   * Applicable to owner, admin, employee, and superadmin.
   */
  fetchFeature: async (
    { commit, rootState },
    payload?: { companyId?: number } | number,
  ) => {
    const companyId =
      (typeof payload === "number" ? payload : payload?.companyId) ||
      rootState.appContext.company?.id ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (rootState as any).superAdmin?.selectedCompany?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    try {
      const features = await CompanyFeaturesService.getCompanyFeatures({
        companyId,
      });
      commit("SET_FEATURES", features);
      return features;
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },

  getCompanyFeatures: async (
    { dispatch },
    payload?: { companyId?: number } | number,
  ) => {
    return dispatch("fetchFeature", payload);
  },

  /**
   * Fetch all features list for superadmin to view and toggle (enable/disable).
   */
  fetchAllFeatures: async (
    { commit, rootState },
    payload?: { companyId?: number } | number,
  ) => {
    const companyId =
      (typeof payload === "number" ? payload : payload?.companyId) ||
      rootState.appContext.company?.id ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (rootState as any).superAdmin?.selectedCompany?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    try {
      const allFeatures = await CompanyFeaturesService.getAllFeatures({
        companyId,
      });
      commit("SET_ALL_FEATURES", allFeatures);
      return allFeatures;
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },

  getAllFeatures: async (
    { dispatch },
    payload?: { companyId?: number } | number,
  ) => {
    return dispatch("fetchAllFeatures", payload);
  },

  /**
   * Enable a feature for a company (Superadmin).
   */
  setCompanyFeature: async (
    { commit, dispatch, rootState },
    payload: { companyId?: number; featureId: number },
  ) => {
    const companyId =
      payload.companyId ||
      rootState.appContext.company?.id ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (rootState as any).superAdmin?.selectedCompany?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    try {
      const result = await CompanyFeaturesService.setCompanyFeature({
        companyId,
        featureId: payload.featureId,
      });
      await dispatch("fetchAllFeatures", companyId);
      await dispatch("fetchFeature", companyId);
      return result;
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },

  /**
   * Disable a feature for a company (Superadmin).
   */
  disableCompanyFeature: async (
    { commit, dispatch, rootState },
    payload: { companyId?: number; featureId: number },
  ) => {
    const companyId =
      payload.companyId ||
      rootState.appContext.company?.id ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (rootState as any).superAdmin?.selectedCompany?.id;
    if (!companyId) {
      commit("alert/SET_SHOW_ERROR", "Company Id is invalid", { root: true });
      return;
    }
    try {
      const result = await CompanyFeaturesService.disableCompanyFeature({
        companyId,
        featureId: payload.featureId,
      });
      await dispatch("fetchAllFeatures", companyId);
      await dispatch("fetchFeature", companyId);
      return result;
    } catch (error) {
      if (error instanceof Error) {
        commit("alert/SET_SHOW_ERROR", error.message, { root: true });
      }
    }
  },
};

export const navigation: Module<NavigationState, RootState> = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export const room = navigation;
export default navigation;
